<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useManual } from '@/setup/manualComposition.js';

const PADDING_X = 50;
const PADDING_Y = 24;
const BUTTON_HEIGHT = 36;
const ITEMS_HEIGHT = 380;
const ITEMS_WIDTH = 402;

const route = useRoute();
const router = useRouter();
const { items, unwatchItemAmount } = useManual();

const pos = ref({
  pos1: 0,
  pos2: 0,
  pos3: 0,
  pos4: 0,
})
const oldPosition = ref({
  left: 0,
  top: 0,
})
const isActive = ref(false);
const floatingBtnRef = ref(null);
const contentRef = ref(null);
const selectedItemIndex = ref(0);
const transitionName = ref('expand-right')

/**
 * Item đc chọn
 * @author: BMThang(03/04/2023)
 */
const selectedItem = computed(() => {
  return items.value[selectedItemIndex.value];
})

/**
 * Route Name của trang hiện tại
 * @author: BMThang(03/04/2023)
 */
const selectedRouteName = computed(() => {
  return route.name;
})

/**
 * Xử lý bấm vào 1 item
 * @param {number} itemIndex Index của item đc bấm
 * @author: BMThang(03/04/2023)
 */
const handleClickItem = (itemIndex) => {
  selectedItemIndex.value = itemIndex;
}

/**
 * Xử lý mở danh sách items
 * @author: BMThang(03/04/2023)
 */
const openItems = () => {
  // Xét active item thành trang hiện tại
  const currentItemIndexByRouteName = items.value.findIndex(item => item.routeName === selectedRouteName.value);
  if (currentItemIndexByRouteName >= 0)
    selectedItemIndex.value = currentItemIndexByRouteName;
  else 
    selectedItemIndex.value = 0;

  const floatingBtnRect = floatingBtnRef.value.getBoundingClientRect();
  if (oldPosition.value.left !== floatingBtnRect.left || oldPosition.value.top !== floatingBtnRect.top) return;
  let left = 0;
  let top = 0;
  let newTransitionName = 'expand-right';
  left = floatingBtnRect.left;
  top = floatingBtnRect.top - ITEMS_HEIGHT + BUTTON_HEIGHT;
  const maxX = PADDING_X + ITEMS_WIDTH;
  if (left + maxX > window.innerWidth) {
    left =  window.innerWidth - maxX;
  }
  if (top < 0) {
    top = PADDING_Y;
  }
  if (floatingBtnRect.left < (window.innerWidth / 2)) {
    newTransitionName = 'expand-left';
  }
  contentRef.value.style.left = `${left}px`;
  contentRef.value.style.top = `${top}px`;
  transitionName.value = newTransitionName;
  isActive.value = true;
}

/**
 * Đóng danh sách items
 * @author: BMThang(03/04/2023)
 */
const closeItems = () => {
  isActive.value = false;
}

/**
 * Xử lý do chuyển chuột
 * @param {object} e event object
 * @author: BMThang(03/04/2023)
 */
const elementDrag = (e) => {
  e = e || window.event;
  e.preventDefault();
  // calculate the new cursor position:
  pos.value.pos1 = pos.value.pos3 - e.clientX;
  pos.value.pos2 = pos.value.pos4 - e.clientY;
  pos.value.pos3 = e.clientX;
  pos.value.pos4 = e.clientY;
  // set the element's new position:
  floatingBtnRef.value.style.top = (floatingBtnRef.value.offsetTop - pos.value.pos2) + "px";
  floatingBtnRef.value.style.left = (floatingBtnRef.value.offsetLeft - pos.value.pos1) + "px";
}

/**
 * Xử lý nhấn chuột
 * @param {object} e event
 * @author: BMThang(03/04/2023)
 */
const dragMouseDown = (e) => {
  e = e || window.event;
  e.preventDefault();
  const floatingBtnRect = floatingBtnRef.value.getBoundingClientRect();
  oldPosition.value.left = floatingBtnRect.left
  oldPosition.value.top = floatingBtnRect.top
  // get the mouse cursor position at startup:
  pos.value.pos3 = e.clientX;
  pos.value.pos4 = e.clientY;
  // call a function whenever the cursor moves:
  document.addEventListener('mousemove', elementDrag);
  if (floatingBtnRef.value) 
    floatingBtnRef.value.style.transition = "width 0.3s ease, height 0.3s ease";
}

/**
 * Xử lý thả chuột
 * @author: BMThang(03/04/2023)
 */
const handleMouseUp = () => {
  document.removeEventListener('mousemove', elementDrag);
  const floatingBtnRect = floatingBtnRef.value.getBoundingClientRect();
  if (pos.value.pos3 < (window.innerWidth / 2)) {
    floatingBtnRef.value.style.left = `${PADDING_X}px`;
  } else {
    floatingBtnRef.value.style.left = `${window.innerWidth - PADDING_X - floatingBtnRect.width}px`;
  }
  if (floatingBtnRect.top < PADDING_Y) {
    floatingBtnRef.value.style.top = `${PADDING_Y}px`;
  } else if (floatingBtnRect.top + floatingBtnRect.height > window.innerHeight - PADDING_Y) {
    floatingBtnRef.value.style.top = `${window.innerHeight - PADDING_Y - floatingBtnRect.height}px`;
  }
  floatingBtnRef.value.style.transition = "0.3s ease-in-out left"
}

/**
 * Xử lý nhấn nút điều hướng
 * @author: BMThang(03/04/2023)
 */
const handleClickNavigateBtn = () => {
  router.push({ name: selectedItem.value.routeName });
}

onUnmounted(() => {
  document.removeEventListener('mousemove', elementDrag);
})

</script>


<template>
  <div 
    ref="floatingBtnRef"
    :class="[
      'manual-floating-btn',
      {'manual-floating-btn-active': isActive},
    ]"
  >
    <div 
      v-if="!isActive"
      class="manual-floating-btn-btn"
      @mousedown="dragMouseDown"
      @mouseup="handleMouseUp"
      @click="openItems"
    >
      <span>
        {{ $t('i18nCommon.UserManual') }}
      </span>
      <div 
        v-if="unwatchItemAmount > 0"
        class="manual-floating-btn-unwatch-amount"
      >
        {{ unwatchItemAmount }}
      </div>
    </div>
  </div>

  <div
    ref="contentRef"
    class="manual-floating-btn-content-wrapper"
  >
    <transition :name="transitionName">
      <div
        v-if="isActive"
        class="manual-floating-btn-content"
      >
        <div class="manual-floating-btn-content-header">
          <div class="manual-floating-btn-content-title">
            {{ $t('i18nCommon.UserManual') }}
          </div>
          <div 
            v-tooltip="$t('i18nCommon.command.close')"
            class="icon20 ic_close-grey-16 manual-floating-btn-content-close" 
            @click="closeItems"
          />
        </div>
        <div class="manual-floating-btn-content-subtitle">
          {{ $t('i18nManual.ManualMenuDesc') }}
        </div>
        <div class="manual-floating-btn-content-items">
          <div
            v-for="(item, itemIndex) in items"
            :key="itemIndex"
            :class="[
              'manual-floating-btn-content-item',
              {'manual-floating-btn-content-item-active': itemIndex === selectedItemIndex},
              {'manual-floating-btn-content-item-current-page': item.routeName === selectedRouteName},
            ]"
            @click="handleClickItem(itemIndex)"
          >
            <div class="manual-floating-btn-content-item-header">
              <div 
                :class="[
                  'icon20 manual-floating-btn-content-item-icon',
                  {'ic_page-viewed': item.isViewed},
                  {'ic_task-process-default': !item.isViewed},
                ]" 
              />
              <div class="manual-floating-btn-content-item-text">
                {{ item.title }}
              </div>
            </div>
            <div 
              v-if="itemIndex === selectedItemIndex"
              class="manual-floating-btn-content-item-body"
            >
              <div class="manual-floating-btn-content-item-desc">
                {{ item.desc }}
              </div>
              <ms-button 
                v-if="item.routeName !== selectedRouteName"
                left-icon="icon20 ic_navigate-right"
                :text="item.btnNavigate"
                @click="handleClickNavigateBtn"
              />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.manual-floating-btn {
  position: fixed;
  top: calc(100vh - 60px);
  left: calc(100vw - 240px);
  z-index: 104;
  .manual-floating-btn-btn {
    position: relative;
    height: 36px;
    min-width: 141px;
    box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.24);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 18px;
    padding: 0 16px;
    background-color: var(--primary);
    font-weight: $font-weight-bold;
    color: var(--white);
    cursor: move;
    .manual-floating-btn-unwatch-amount {
      position: absolute;
      top: -5px;
      right: 0;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: var(--red);
      color: var(--white);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: $font-size-caption;
      font-weight: $font-weight-medium;
      line-height: 0;
    }
  }
}

.manual-floating-btn-content-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 106;
  .manual-floating-btn-content {
    width: 402px;
    height: 380px;
    border-radius: 4px;
    padding: 16px;
    box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.24);
    background-color: var(--white);
    .manual-floating-btn-content-header {
      height: 36px;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .manual-floating-btn-content-title {
        font-size: $font-size-heading-2;
        font-weight: $font-weight-bold;
      }
      .manual-floating-btn-content-close {
        width: 36px;
        height: 36px;
      }
    }
    .manual-floating-btn-content-subtitle {
      margin-bottom: 16px;
    }
    .manual-floating-btn-content-items {
      .manual-floating-btn-content-item {
        margin-bottom: 16px;
        padding: 4px 0;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s ease;
        .manual-floating-btn-content-item-header {
          display: flex;
          align-items: center;
          height: 24px;
          padding-left: 12px;
          border-left: 4px solid transparent;
          column-gap: 20px;
          .manual-floating-btn-content-item-icon {
            height: 24px;
            width: 24px;
          }
          .manual-floating-btn-content-item-text {
            font-size: $font-size-heading-3;
            transition: color 0.2s ease;
          }
        }
        .manual-floating-btn-content-item-body {
          overflow: hidden;
          margin-top: 4px;
          padding-left: 60px;
          .manual-floating-btn-content-item-desc {
            margin-bottom: 16px;
          }
        }
        &:last-child {
          margin-bottom: 0;
        }
        &:hover {
          background-color: var(--ms-semi-grey-blue);
        }
        &.manual-floating-btn-content-item-active {
          cursor: default;
          .manual-floating-btn-content-item-header { 
            border-left: 4px solid var(--primary);
            .manual-floating-btn-content-item-text {
              font-weight: $font-weight-bold;
              color: var(--primary);
            }
          }
          &:hover {
            background-color: transparent;
          }
        }
        &.manual-floating-btn-content-item-current-page {
          .manual-floating-btn-content-item-body {
            .manual-floating-btn-content-item-desc {
              margin-bottom: 0;
            }
          }
        }
      }
    }
  }
}

// expand-right
.expand-right-enter-active {
  transform-origin: right bottom;
  transition: transform 0.3s cubic-bezier(.25,.8,.25,1), 0.3s opacity ease;
}

.expand-right-leave-active {
  transform-origin: right bottom;
  transition: transform 0.3s cubic-bezier(.25,.8,.25,1), 0.3s opacity ease;
}

.expand-right-enter-from {
  transform: scale(0.35, 0.1);
  opacity: 0;
}

.expand-right-leave-to {
  transform: scale(0.35, 0.1);
  opacity: 0;
}

// expand-left
.expand-left-enter-active {
  transform-origin: left bottom;
  transition: transform 0.3s cubic-bezier(.25,.8,.25,1), 0.3s opacity ease;
}

.expand-left-leave-active {
  transform-origin: left bottom;
  transition: transform 0.3s cubic-bezier(.25,.8,.25,1), 0.3s opacity ease;
}

.expand-left-enter-from {
  transform: scale(0.35, 0.1);
  opacity: 0;
}

.expand-left-leave-to {
  transform: scale(0.35, 0.1);
  opacity: 0;
}
</style>